import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, catchError, of } from "rxjs";
import { SystemApiService } from "@cv-invision/core";

@Injectable()
export class OutgoingEventsHybridService {
	constructor(
		private httpClient: HttpClient,
		private systemApiService: SystemApiService
	) { }

	/**
	 * Retrieves the list of all integrations.
	 * @param params Optional parameters for the request, such as a limit on the number of results.
	 * @returns An observable containing the list of integrations.
	 */
	getIntegrationList(params: { limit?: number } = {}): Observable<any> {
		let httpParams = new HttpParams();
		if (params.limit) {
			httpParams = httpParams.set('limit', params.limit.toString());
		}

		return this.httpClient.get(
			`${this.systemApiService.api.orders}/utils/integrations/list-all`,
			{ params: httpParams }
		);
	}

	/**
	 * Retrieves the list of all non-generic integrations.
	 * @param params Optional parameters for the request, such as a limit on the number of results.
	 * @returns An observable containing the list of non-generic integrations.
	 */
	getIntegrationListNonGeneric(params: { limit?: number } = {}): Observable<any> {
		let httpParams = new HttpParams();
		if (params.limit) {
			httpParams = httpParams.set('limit', params.limit.toString());
		}

		return this.httpClient.get(
			`${this.systemApiService.api.orders}/utils/integrations/list-all-non-generic`,
			{ params: httpParams }
		);
	}

	/**
	 * Fetches outgoing event configuration for a specific integration by its name.
	 * @param integrationName The name of the integration.
	 * @returns An observable with the integration's outgoing event data.
	 */
	getIntegrationOutgoingEvents(integrationName: string): Observable<any> {
		return this.httpClient.get(
			`${this.systemApiService.api.orders}/utils/integrations/${integrationName.toLowerCase()}/outgoing-events`
		);
	}

	/**
	 * Fetches outgoing event configuration for a specific integration by organization ID.
	 * @param orgId The organization ID tied to the integration.
	 * @returns An observable with the integration's outgoing event data.
	 */
	getIntegrationOutgoingEventsbyOrganizationId(orgId: string): Observable<any> {
		return this.httpClient.get(
			`${this.systemApiService.api.orders}/utils/integrations/${orgId}/outgoing-events`
		);
	}

	/**
	 * Creates outgoing events for a given integration.
	 * @param integrationName The name of the integration.
	 * @param payload The event data to save.
	 * @returns An observable with the response, or null if an error occurs.
	 */
	createOutgoingEvents(integrationName: string, payload: any): Observable<any> {
		const url = `${this.systemApiService.api.orders}/utils/integrations/${integrationName.toLowerCase()}/outgoing-events`;
		return this.httpClient.put(url, payload).pipe(
			catchError((error) => {
				console.error("Error creating outgoing events:", error);
				return of(null);
			})
		);
	}

	/**
	 * Updates existing outgoing events for a given integration.
	 * @param integrationName The name of the integration.
	 * @param payload The updated event data.
	 * @returns An observable with the response, or null if an error occurs.
	 */
	updateOutgoingEvents(integrationName: string, payload: any): Observable<any> {
		const url = `${this.systemApiService.api.orders}/utils/integrations/${integrationName.toLowerCase()}/outgoing-events`;
		return this.httpClient.put(url, payload).pipe(
			catchError((error) => {
				console.error("Error updating outgoing events:", error);
				return of(null);
			})
		);
	}

	/**
	 * Saves outgoing events for an integration. Decides whether to create or update based on `isEditMode`.
	 * @param isEditMode Flag indicating whether this is an update (true) or a creation (false).
	 * @param integrationName The name of the integration.
	 * @param payload The event data to be saved.
	 * @returns An observable with the response, or null if an error occurs.
	 */
	saveOutgoingEvents(isEditMode: boolean, integrationName: string, payload: any): Observable<any> {
		const method = isEditMode ? 'update' : 'create';
		const url = `${this.systemApiService.api.orders}/utils/integrations/${integrationName.toLowerCase()}/outgoing-events`;
		return this.httpClient.put(url, payload).pipe(
			catchError((error) => {
				console.error(`Error ${method} outgoing events:`, error);
				return of(null);
			})
		);
	}
}
